import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiError, API } from "../lib/api";
import AppLayout from "../components/AppLayout";
import Countdown from "../components/Countdown";
import { toast } from "sonner";
import {
  Rocket, Lock, Unlock, Loader2, ArrowLeft, Sparkles, Calendar as CalIcon,
  FileText, Image as ImageIcon, Video, Mic, File as FileIcon, Link as LinkIcon,
  Package, Trash2, XCircle, Gift, Download, ShieldCheck, ShieldAlert
} from "lucide-react";
import { STATE_LABEL, formatDateTime, COVER_THEMES, CATEGORIES } from "../lib/constants";
import BlockchainStatusBadge from "../components/BlockchainStatusBadge";
import {
  getLocalCapsule, previewLocalContent, getLocalFinalMessage,
  openSealedCapsule, revokeLocalCapsule,
} from "../lib/clientVault";

import { _t, _locale } from "@/lib/i18n";
const V2_WINDOW_WARNING =
  "La capsula è cifrata e inviolabile anche per noi. " +
  "Puoi revocarla gratuitamente entro 24 ore; dopo verrà sigillata su blockchain in modo permanente.";

function V2WindowWarning() {
  return (
    <div className="mb-6 p-4 rounded-xl border border-yellow-400/30 bg-yellow-400/5 flex items-start gap-3" data-testid="v2-window-warning">
      <ShieldAlert className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
      <div className="text-sm text-white/85 leading-relaxed">
        <strong className="text-yellow-300">{_t("Capsula sigillata solo sul tuo dispositivo.")}</strong>{" "}
        {V2_WINDOW_WARNING}
      </div>
    </div>
  );
}

export default function CapsuleDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [cap, setCap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [preopened, setPreopened] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/capsules/${id}`);
      setCap(data);
    } catch (e) {
      toast.error(formatApiError(e));
      nav("/dashboard");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const handleOpen = async () => {
    setOpening(true);
    try {
      // Per capsule v2 l'apertura avviene esclusivamente client-side.
      if (cap?.security_model === "client_sealed_v2") {
        // Prima decifriamo sul dispositivo: solo se riesce segnaliamo l'apertura al server.
        const result = await openSealedCapsule(api, id);
        setPreopened(result);
        const { data } = await api.post(`/capsules/${id}/open`);
        setCap(data);
        toast.success(_t("Capsula aperta"));
      } else {
        const { data } = await api.post(`/capsules/${id}/open`);
        setCap(data);
        toast.success(_t("Capsula aperta"));
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOpening(false); }
  };

  const handleCancel = async () => {
    if (!confirm(_t("Annullare il lancio? Se la capsula è già ancorata su Polygon Amoy verrà revocata on-chain in modo permanente."))) return;
    setCancelling(true);
    try {
      const { data } = await api.post(`/capsules/${id}/cancel`);
      // Per v2: rimuoviamo tutte le tracce locali (IndexedDB) alla revoca.
      if (cap?.security_model === "client_sealed_v2") {
        try { await revokeLocalCapsule(id); } catch (_e) {}
      }
      setCap(data);
      if (data.state === "REVOCATA") {
        if (data.blockchain_revoke_tx_hash) {
          toast.success(_t("Capsula revocata on-chain"));
        } else {
          toast.success(_t("Capsula revocata"));
        }
      } else {
        toast.success(_t("Lancio annullato"));
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCancelling(false); }
  };

  const handleDelete = async () => {
    if (!confirm(_t("Eliminare la bozza definitivamente?"))) return;
    try {
      await api.delete(`/capsules/${id}`);
      if (cap?.security_model === "client_sealed_v2") {
        try { await revokeLocalCapsule(id); } catch (_e) {}
      }
      toast.success(_t("Bozza eliminata"));
      nav("/dashboard");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading || !cap) {
    return <AppLayout><div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#FF5722]" /></div></AppLayout>;
  }

  const coverClass = COVER_THEMES.find(t => t.id === cap.cover_theme)?.className || "cover-orbit";
  const catLabel = CATEGORIES.find(c => c.id === cap.category)?.label || cap.category;
  const isOwner = cap.role === "owner";
  const isRecipient = cap.role === "recipient";
  const state = cap.state;

  return (
    <AppLayout>
      <button onClick={() => nav("/dashboard")} className="btn-ghost mb-8" data-testid="back-btn"><ArrowLeft className="w-4 h-4" />{" "}{_t("Torna alla dashboard")}</button>

      {/* Cover header */}
      <div className={`${coverClass} rounded-3xl p-8 md:p-12 mb-8 border border-white/10`}>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="text-[10px] uppercase tracking-[0.3em] text-white/60 px-3 py-1 rounded-full border border-white/20">{catLabel}</span>
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#FF5722] px-3 py-1 rounded-full border border-[#FF5722]/40">{STATE_LABEL[state]}</span>
        </div>
        <h1 data-testid="detail-title" className="font-display text-5xl md:text-6xl text-white leading-tight mb-3">{cap.title}</h1>
        {cap.description && <p className="text-white/70 max-w-2xl">{cap.description}</p>}
        <div className="mt-6 flex flex-wrap gap-6 text-xs text-white/60">
          {cap.open_at && <div className="flex items-center gap-2"><CalIcon className="w-3.5 h-3.5" />{" "}{_t("Apertura:")}{" "}<span className="text-white">{formatDateTime(cap.open_at)}</span></div>}
          <div>{_t("Destinatari:")}{" "}<span className="text-white">{cap.recipients_count}</span></div>
          <div>{_t("Contenuti:")}{" "}<span className="text-white">{cap.content_count}</span></div>
        </div>
      </div>

      {/* State-driven views */}
      {state === "BOZZA" && isOwner && (cap.security_model === "client_sealed_v2"
        ? <DraftV2View cap={cap} onDelete={handleDelete} nav={nav} />
        : <DraftView cap={cap} onDelete={handleDelete} nav={nav} />)}
      {state === "RIPENSAMENTO_24H" && isOwner && (
        <>
          {cap.security_model === "client_sealed_v2" && <V2WindowWarning />}
          <ReconsiderView cap={cap} onCancel={handleCancel} cancelling={cancelling} onDone={load} />
        </>
      )}
      {state === "IN_VIAGGIO" && isOwner && <TravelingOwnerView cap={cap} onDone={load} />}
      {state === "IN_VIAGGIO" && isRecipient && <SealedRecipientView cap={cap} onDone={load} />}
      {state === "DISPONIBILE" && isRecipient && <ReadyView onOpen={handleOpen} opening={opening} />}
      {state === "DISPONIBILE" && isOwner && <DeliveredOwnerView cap={cap} />}
      {(state === "APERTA") && isRecipient && (cap.security_model === "client_sealed_v2"
        ? <OpenedV2View cap={cap} initial={preopened} />
        : <OpenedView cap={cap} />)}
      {(state === "APERTA") && isOwner && <OpenedOwnerView cap={cap} />}
      {state === "REVOCATA" && <RevokedView cap={cap} isOwner={isOwner} />}
    </AppLayout>
  );
}

function RevokedView({ cap, isOwner }) {
  const tx = cap.blockchain_revoke_tx_hash;
  const polygonUrl = tx ? `https://amoy.polygonscan.com/tx/${tx}` : null;
  return (
    <div className="tc-card p-8 md:p-12" data-testid="revoked-view">
      <div className="flex items-start gap-3 mb-6">
        <XCircle className="w-6 h-6 text-red-400" />
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-red-400 mb-1">{_t("Capsula revocata")}</div>
          <h2 className="font-display text-3xl text-white mb-2">{_t("Il lancio è stato annullato in modo permanente")}</h2>
          <p className="text-white/60 max-w-lg">
            {isOwner
              ? _t("Hai revocato questa capsula durante la finestra di ripensamento. L'evidenza è registrata on-chain e non può più essere aperta da nessun destinatario.")
              : _t("Il mittente ha revocato questa capsula durante la finestra di ripensamento. Non sarà consegnata.")}
          </p>
        </div>
      </div>
      {cap.revoked_at && (
        <div className="text-xs text-white/50 mb-3">{_t("Revocata il {0}", formatDateTime(cap.revoked_at))}</div>
      )}
      {tx && (
        <div className="mt-4 p-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5">
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400 mb-2">{_t("Evidenza blockchain")}</div>
          <div className="text-xs text-white/70 mb-2 break-all" data-testid="revoke-tx-hash">
            <span className="text-white/50">{_t("Tx hash:")}{" "}</span>{tx}
          </div>
          {cap.blockchain_revoke_block_number && (
            <div className="text-xs text-white/50 mb-2">{_t("Blocco: {0}", cap.blockchain_revoke_block_number)}</div>
          )}
          {polygonUrl && (
            <a href={polygonUrl} target="_blank" rel="noopener noreferrer"
               className="btn-secondary !text-xs !py-2 !px-3 inline-flex" data-testid="revoke-tx-link">
              <ShieldCheck className="w-3.5 h-3.5" />{" "}{_t("Verifica su Polygonscan")}</a>
          )}
        </div>
      )}
    </div>
  );
}

function DraftView({ cap, onDelete, nav }) {
  return (
    <div className="tc-card p-8 md:p-12">
      <div className="flex items-start gap-3 mb-6">
        <Package className="w-6 h-6 text-white/50" />
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-white/50 mb-1">{_t("Bozza")}</div>
          <h2 className="font-display text-3xl text-white mb-2">{_t("La tua capsula è ancora in preparazione")}</h2>
          <p className="text-white/60 max-w-lg">{_t("Completa il wizard per lanciarla. Puoi modificarla liberamente finché è in bozza.")}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 mt-6">
        <button onClick={() => nav(`/wizard/${cap.id}`)} className="btn-launch" data-testid="continue-wizard-btn"><Rocket className="w-4 h-4 -rotate-45" />{" "}{_t("Continua a preparare")}</button>
        <button onClick={onDelete} className="btn-danger" data-testid="delete-draft-btn"><Trash2 className="w-4 h-4" />{" "}{_t("Elimina bozza")}</button>
      </div>
    </div>
  );
}

function DraftV2View({ cap, onDelete, nav }) {
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { setRec(await getLocalCapsule(cap.id)); } catch (_e) {}
      finally { setLoading(false); }
    })();
  }, [cap.id]);

  const localCount = (rec?.contents?.length || 0) + (rec?.finalMessage ? 1 : 0);

  return (
    <div className="tc-card p-8 md:p-12" data-testid="draft-v2-view">
      <div className="flex items-start gap-3 mb-6">
        <Package className="w-6 h-6 text-white/50" />
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-white/50 mb-1">{_t("Bozza · cifrata sul dispositivo")}</div>
          <h2 className="font-display text-3xl text-white mb-2">{_t("La tua capsula è ancora in preparazione")}</h2>
          <p className="text-white/60 max-w-lg">
            {loading
              ? _t("Carico i contenuti cifrati locali…")
              : rec
                ? _t("Hai {0} elemento{1} salvato{2} in locale. I contenuti non sono stati inviati al server.", localCount, localCount === 1 ? "" : "i", localCount === 1 ? "" : "i")
                : _t("Non ho trovato il vault locale su questo dispositivo. Se stai usando un altro device, i contenuti precedenti sono rimasti sull'altro.")}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 mt-6">
        <button onClick={() => nav(`/wizard/${cap.id}`)} className="btn-launch" data-testid="continue-wizard-btn"><Rocket className="w-4 h-4 -rotate-45" />{" "}{_t("Continua a preparare")}</button>
        <button onClick={onDelete} className="btn-danger" data-testid="delete-draft-btn"><Trash2 className="w-4 h-4" />{" "}{_t("Elimina bozza")}</button>
      </div>
    </div>
  );
}

function ReconsiderView({ cap, onCancel, cancelling, onDone }) {
  const inProgress = cap.revoke_in_progress || cancelling;
  const willRevokeOnchain = cap.blockchain_status === "CONFIRMED";
  return (
    <div className="tc-card p-8 md:p-12 relative overflow-hidden">
      <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full bg-yellow-400/10 blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="text-[11px] uppercase tracking-[0.3em] text-yellow-400 mb-2">{_t("Periodo di ripensamento")}</div>
        <h2 className="font-display text-4xl text-white mb-3">{_t("Hai ancora tempo per ripensarci")}</h2>
        <p className="text-white/70 mb-6 max-w-xl">
          {willRevokeOnchain
            ? _t("Puoi revocare on-chain il lancio: l'evidenza sarà registrata su Polygon Amoy in modo permanente e la capsula non potrà più essere aperta. Trascorse le 24 ore la revoca non sarà più possibile.")
            : _t("Puoi annullare il lancio e riportare la capsula in bozza. Trascorse le 24 ore sarà sigillata e non più modificabile.")}
        </p>
        <div className="mb-2 text-[10px] uppercase tracking-[0.3em] text-white/40">{_t("Puoi annullare entro")}</div>
        <Countdown target={cap.reconsideration_ends_at} onDone={onDone} size="md" glow={false} />
        <div className="mt-8">
          <button data-testid="cancel-launch-btn" onClick={onCancel} disabled={inProgress} className="btn-danger">
            {inProgress
              ? <><Loader2 className="w-4 h-4 animate-spin" /> {willRevokeOnchain ? _t("Revoca on-chain in corso...") : _t("Annullamento...")}</>
              : <><XCircle className="w-4 h-4" /> {willRevokeOnchain ? _t("Revoca on-chain") : _t("Annulla il lancio")}</>}
          </button>
        </div>
        <div className="mt-6"><BlockchainStatusBadge cap={cap} /></div>
      </div>
    </div>
  );
}

function TravelingOwnerView({ cap, onDone }) {
  return (
    <div className="tc-card p-8 md:p-12 text-center">
      <div className="inline-flex w-20 h-20 items-center justify-center rounded-full bg-gradient-to-br from-[#33B2FF]/20 to-[#33B2FF]/5 border border-[#33B2FF]/30 mb-6">
        <Rocket className="w-9 h-9 text-[#33B2FF] -rotate-45 rocket-shake" />
      </div>
      <div className="text-[11px] uppercase tracking-[0.3em] text-[#33B2FF] mb-3">{_t("In viaggio")}</div>
      <h2 className="font-display text-3xl md:text-4xl text-white mb-4">{_t("La tua capsula sta viaggiando nel tempo")}</h2>
      <p className="text-white/60 max-w-lg mx-auto mb-8">{_t("Come mittente, non puoi più accedere ai contenuti. Sarà consegnata al destinatario alla data prevista.")}</p>
      <div className="mb-3 text-[10px] uppercase tracking-[0.3em] text-white/40">{_t("Manca all'apertura")}</div>
      <Countdown target={cap.open_at} onDone={onDone} />
      <div className="mt-8 space-y-3">
        <BlockchainStatusBadge cap={cap} />
        <AnchorPanel capsuleId={cap.id} />
      </div>
    </div>
  );
}

function SealedRecipientView({ cap, onDone }) {
  const senderName = cap.recipients?.[0]?.personal_message; // recipient view doesn't show sender explicitly but we can show personal_message
  return (
    <div className="tc-card p-8 md:p-12 text-center">
      <div className="inline-flex w-20 h-20 items-center justify-center rounded-full border border-white/15 bg-white/5 mb-6">
        <Lock className="w-9 h-9 text-white/70" />
      </div>
      <div className="text-[11px] uppercase tracking-[0.3em] text-white/50 mb-3">{_t("Sigillata")}</div>
      <h2 className="font-display text-3xl md:text-4xl text-white mb-4">{_t("Una capsula sta arrivando per te")}</h2>
      <p className="text-white/60 max-w-lg mx-auto mb-8">{_t("Si aprirà automaticamente il")}{" "}<span className="text-white">{formatDateTime(cap.open_at)}</span>.</p>
      <div className="mb-3 text-[10px] uppercase tracking-[0.3em] text-white/40">{_t("Manca")}</div>
      <Countdown target={cap.open_at} onDone={onDone} />
      <div className="mt-8 space-y-3">
        <BlockchainStatusBadge cap={cap} />
        <AnchorPanel capsuleId={cap.id} />
      </div>
    </div>
  );
}

function ReadyView({ onOpen, opening }) {
  return (
    <div className="tc-card p-8 md:p-16 text-center relative overflow-hidden">
      <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full bg-[#FF5722]/20 blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="inline-flex w-20 h-20 items-center justify-center rounded-full bg-gradient-to-br from-[#FF5722] to-[#FF8A50] mb-6 shadow-lg shadow-[#FF5722]/40">
          <Sparkles className="w-9 h-9 text-white" />
        </div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5722] mb-3">{_t("Il momento è arrivato")}</div>
        <h2 className="font-display text-4xl md:text-5xl text-white mb-4">{_t("Puoi aprire la tua Time Capsule")}</h2>
        <p className="text-white/60 max-w-md mx-auto mb-10">{_t("Un attimo, poi tutto si aprirà davanti a te.")}</p>
        <button data-testid="open-capsule-btn" onClick={onOpen} disabled={opening} className="btn-launch text-base !px-8">
          {opening ? <><Loader2 className="w-5 h-5 animate-spin" />{" "}{_t("Apro...")}</> : <><Unlock className="w-5 h-5" />{" "}{_t("Apri la capsula")}</>}
        </button>
      </div>
    </div>
  );
}

function DeliveredOwnerView({ cap }) {
  return (
    <div className="tc-card p-8 md:p-12 text-center">
      <div className="inline-flex w-20 h-20 items-center justify-center rounded-full border border-[#FF5722]/40 bg-[#FF5722]/10 mb-6">
        <Sparkles className="w-9 h-9 text-[#FF5722]" />
      </div>
      <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5722] mb-3">{_t("Consegnata")}</div>
      <h2 className="font-display text-3xl md:text-4xl text-white mb-3">{_t("La capsula è pronta per il destinatario")}</h2>
      <p className="text-white/60 max-w-lg mx-auto">{_t("Riceverà una notifica per aprirla. Ti aggiorneremo quando lo farà.")}</p>
    </div>
  );
}

function OpenedOwnerView({ cap }) {
  return (
    <div className="tc-card p-8 md:p-12 text-center">
      <div className="inline-flex w-20 h-20 items-center justify-center rounded-full border border-green-400/30 bg-green-400/10 mb-6">
        <Unlock className="w-9 h-9 text-green-400" />
      </div>
      <div className="text-[11px] uppercase tracking-[0.3em] text-green-400 mb-3">{_t("Aperta")}</div>
      <h2 className="font-display text-3xl md:text-4xl text-white mb-3">{_t("Il tuo destinatario ha aperto la capsula")}</h2>
      <p className="text-white/60 max-w-lg mx-auto">{_t("Aperta il {0}.", formatDateTime(cap.opened_at))}</p>
    </div>
  );
}

export function OpenedV2View({ cap, loader, initial, showAnchor = true }) {
  const [state, setState] = useState(initial
    ? { loading: false, error: null, contents: initial.contents || [], finalMessage: initial.final_message || "" }
    : { loading: true, error: null, contents: [], finalMessage: "" });

  useEffect(() => {
    if (initial) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await (loader ? loader() : openSealedCapsule(api, cap.id));
        if (cancelled) return;
        setState({ loading: false, error: null, contents: result.contents || [], finalMessage: result.final_message || "" });
      } catch (e) {
        if (cancelled) return;
        setState({ loading: false, error: String(e?.message || e), contents: [], finalMessage: "" });
      }
    })();
    return () => { cancelled = true; };
  }, [cap.id]);

  if (state.loading) {
    return (
      <div className="tc-card p-8 md:p-12 text-center" data-testid="opened-v2-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#FF5722] mx-auto" />
        <div className="text-white/60 text-sm mt-3">{_t("Decifro la capsula sul tuo dispositivo…")}</div>
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="tc-card p-8 md:p-12 border-red-500/30" data-testid="opened-v2-error">
        <div className="text-red-300 font-medium mb-2">{_t("Decifratura non riuscita")}</div>
        <div className="text-white/70 text-sm">{state.error}</div>
        <div className="text-white/40 text-xs mt-3">{_t("La chiave è disponibile solo quando il round drand target è stato firmato. Riprova tra qualche istante.")}</div>
      </div>
    );
  }

  const textItems = state.contents.filter(c => c.kind === "text");
  const linkItems = state.contents.filter(c => c.kind === "link");
  const photos = state.contents.filter(c => c.kind === "photo");
  const videos = state.contents.filter(c => c.kind === "video");
  const audios = state.contents.filter(c => c.kind === "audio");
  const docs = state.contents.filter(c => c.kind === "document");

  const blobUrl = (c) => URL.createObjectURL(c.blob);
  const download = (c) => {
    const url = URL.createObjectURL(c.blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = c.filename || c.title || "file";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="fade-up" data-testid="opened-v2-view">
      {(state.contents.length > 0 || state.finalMessage) && (
        <div className="flex justify-end mb-4">
          <button data-testid="download-all-btn" onClick={() => downloadAllAsZip(cap.title, state)} className="btn-launch !text-sm">
            <Download className="w-4 h-4" />{" "}{_t("Scarica tutto")}</button>
        </div>
      )}
      {state.finalMessage && (
        <div className="tc-card p-8 md:p-12 mb-6" data-testid="opened-final-message">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">{_t("Il messaggio finale")}</div>
          <div className="font-hand text-3xl md:text-4xl text-white leading-relaxed whitespace-pre-wrap">{state.finalMessage}</div>
        </div>
      )}

      {textItems.map((c) => (
        <div key={c.id} className="tc-card p-6 md:p-8 mb-4">
          {c.title && <div className="font-display text-2xl text-white mb-2">{c.title}</div>}
          <div className="text-white/85 leading-relaxed whitespace-pre-wrap">{c.text}</div>
        </div>
      ))}

      {linkItems.length > 0 && (
        <div className="tc-card p-6 md:p-8 mb-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">{_t("Link")}</div>
          <ul className="space-y-2">
            {linkItems.map((c) => (
              <li key={c.id}>
                <a href={c.text} target="_blank" rel="noreferrer" className="text-[#FF5722] hover:underline flex items-center gap-2">
                  <LinkIcon className="w-4 h-4" /> {c.title || c.text}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {photos.length > 0 && (
        <div className="tc-card p-6 md:p-8 mb-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">{_t("Foto")}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="opened-photos">
            {photos.map((c) => (
              <div key={c.id} className="rounded-xl overflow-hidden border border-white/10 aspect-square bg-black">
                <img src={blobUrl(c)} alt={c.filename || c.title} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      )}

      {videos.length > 0 && (
        <div className="tc-card p-6 md:p-8 mb-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">{_t("Video")}</div>
          <div className="grid md:grid-cols-2 gap-4" data-testid="opened-videos">
            {videos.map((c) => (
              <video key={c.id} src={blobUrl(c)} controls className="w-full rounded-xl bg-black" />
            ))}
          </div>
        </div>
      )}

      {audios.length > 0 && (
        <div className="tc-card p-6 md:p-8 mb-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">{_t("Audio")}</div>
          <div className="space-y-3">
            {audios.map((c) => (
              <div key={c.id} className="p-4 rounded-xl border border-white/10">
                {c.title && <div className="text-sm text-white mb-2">{c.title}</div>}
                <audio src={blobUrl(c)} controls className="w-full" />
              </div>
            ))}
          </div>
        </div>
      )}

      {docs.length > 0 && (
        <div className="tc-card p-6 md:p-8 mb-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">{_t("Documenti")}</div>
          <ul className="space-y-2">
            {docs.map((c) => (
              <li key={c.id} className="flex items-center justify-between p-3 rounded-xl border border-white/10">
                <div className="flex items-center gap-3">
                  <FileIcon className="w-4 h-4 text-[#FF5722]" />
                  <div className="text-sm text-white truncate">{c.title || c.filename}</div>
                </div>
                <button onClick={() => download(c)} className="btn-ghost !py-1 !px-3 text-xs">{_t("Scarica")}</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {state.contents.length === 0 && !state.finalMessage && (
        <div className="text-white/50 italic text-center py-10">{_t("Questa capsula è vuota.")}</div>
      )}

      {showAnchor && <div className="mt-6"><AnchorPanel capsuleId={cap.id} /></div>}
    </div>
  );
}

export async function downloadAllAsZip(title, { contents, finalMessage }) {
  const { default: JSZip } = await import("jszip");
  const zip = new JSZip();
  const safe = (s) => String(s).replace(/[\\/:*?"<>|]/g, "_");
  if (finalMessage) zip.file("messaggio-finale.txt", finalMessage);
  contents.forEach((c, i) => {
    const n = String(i + 1).padStart(2, "0");
    if (c.kind === "text" || c.kind === "link") zip.file(`${n}-${safe(c.title || c.kind)}.txt`, c.text || "");
    else if (c.blob) zip.file(`${n}-${safe(c.filename || c.title || "file")}`, c.blob);
  });
  const blob = await zip.generateAsync({ type: "blob" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${safe(title || "capsula")}.zip`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function OpenedView({ cap }) {
  const token = typeof window !== "undefined" ? localStorage.getItem("tc_access_token") : "";
  const buildUrl = (c) => `${API}/capsules/${cap.id}/contents/${c.id}/download${token ? `?auth=${encodeURIComponent(token)}` : ""}`;

  const contents = cap.contents || [];
  const textItems = contents.filter(c => c.kind === "text");
  const photos = contents.filter(c => c.kind === "photo");
  const videos = contents.filter(c => c.kind === "video");
  const audios = contents.filter(c => c.kind === "audio");
  const docs = contents.filter(c => c.kind === "document");
  const links = contents.filter(c => c.kind === "link");
  const vouchers = contents.filter(c => c.kind === "voucher");

  return (
    <div className="fade-up">
      {cap.final_message && (
        <div className="tc-card p-8 md:p-12 mb-6" data-testid="opened-final-message">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">{_t("Il messaggio finale")}</div>
          <div className="font-hand text-3xl md:text-4xl text-white leading-relaxed whitespace-pre-wrap">{cap.final_message}</div>
        </div>
      )}

      <PrivateReplyBlock capsuleId={cap.id} />

      {vouchers.length > 0 && (
        <div className="mb-6" data-testid="opened-vouchers">
          {vouchers.map((v) => <VoucherCard key={v.id} content={v} capsuleId={cap.id} />)}
        </div>
      )}

      {textItems.map((c) => (
        <div key={c.id} className="tc-card p-6 md:p-8 mb-4">
          {c.title && <div className="font-display text-2xl text-white mb-2">{c.title}</div>}
          <div className="text-white/85 leading-relaxed whitespace-pre-wrap">{c.text_body}</div>
        </div>
      ))}

      {photos.length > 0 && (
        <div className="tc-card p-6 md:p-8 mb-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">{_t("Foto")}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="opened-photos">
            {photos.map((c) => (
              <div key={c.id} className="rounded-xl overflow-hidden border border-white/10 aspect-square bg-black">
                <img src={buildUrl(c)} alt={c.original_filename} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      )}

      {videos.length > 0 && (
        <div className="tc-card p-6 md:p-8 mb-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">{_t("Video")}</div>
          <div className="grid md:grid-cols-2 gap-4" data-testid="opened-videos">
            {videos.map((c) => (
              <video key={c.id} src={buildUrl(c)} controls className="w-full rounded-xl bg-black" />
            ))}
          </div>
        </div>
      )}

      {audios.length > 0 && (
        <div className="tc-card p-6 md:p-8 mb-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">{_t("Audio")}</div>
          <div className="space-y-3">
            {audios.map((c) => (
              <div key={c.id} className="p-4 rounded-xl border border-white/10">
                {c.title && <div className="text-sm text-white mb-2">{c.title}</div>}
                <audio src={buildUrl(c)} controls className="w-full" />
              </div>
            ))}
          </div>
        </div>
      )}

      {docs.length > 0 && (
        <div className="tc-card p-6 md:p-8 mb-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">{_t("Documenti")}</div>
          <ul className="space-y-2">
            {docs.map((c) => (
              <li key={c.id} className="flex items-center justify-between p-3 rounded-xl border border-white/10">
                <div className="flex items-center gap-3">
                  <FileIcon className="w-4 h-4 text-[#FF5722]" />
                  <div className="text-sm text-white truncate">{c.title || c.original_filename}</div>
                </div>
                <a href={buildUrl(c)} target="_blank" rel="noreferrer" className="btn-ghost !py-1 !px-3 text-xs">{_t("Scarica")}</a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {links.length > 0 && (
        <div className="tc-card p-6 md:p-8 mb-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">{_t("Link")}</div>
          <ul className="space-y-2">
            {links.map((c) => (
              <li key={c.id}>
                <a href={c.url} target="_blank" rel="noreferrer" className="text-[#FF5722] hover:underline flex items-center gap-2">
                  <LinkIcon className="w-4 h-4" /> {c.title || c.url}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {contents.length === 0 && !cap.final_message && (
        <div className="text-white/50 italic text-center py-10">{_t("Questa capsula è vuota.")}</div>
      )}

      <div className="mt-6"><AnchorPanel capsuleId={cap.id} /></div>
    </div>
  );
}

// -------------- Anchor panel: certificate download + verify link --------------
function AnchorPanel({ capsuleId }) {
  const [anchor, setAnchor] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = typeof window !== "undefined" ? localStorage.getItem("tc_access_token") : "";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/capsules/${capsuleId}/anchor`);
        setAnchor(data);
      } catch {}
      finally { setLoading(false); }
    })();
  }, [capsuleId]);

  if (loading || !anchor) return null;
  const certUrl = `${API}/capsules/${capsuleId}/certificate?auth=${encodeURIComponent(token || "")}`;
  const verifyUrl = `/verifica-sigillo/${anchor.anchor_id}`;

  return (
    <div className="tc-card p-5 text-left" data-testid="anchor-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#33B2FF] mb-2 flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" />{" "}{_t("Certificato di sigillo blockchain")}</div>
          <div className="text-white text-sm">{_t("Anchor ID:")}{" "}<span className="font-mono-launch text-white/80">{anchor.anchor_id}</span></div>
          <div className="text-xs text-white/50 mt-1">{_t("Block #{0} · {1}", anchor.block_number, anchor.chain)}</div>
          {anchor.is_demo && anchor.provider !== "polygon_amoy" && <div className="text-[10px] text-red-300 mt-2">{_t("⚠ SIGILLO DEMO — non su blockchain pubblica")}</div>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href={certUrl} target="_blank" rel="noreferrer" className="btn-ghost !text-sm" data-testid="download-certificate-btn">
            <Download className="w-4 h-4" />{" "}{_t("Scarica certificato")}</a>
          <a href={verifyUrl} target="_blank" rel="noreferrer" className="btn-ghost !text-sm" data-testid="anchor-verify-link">
            <ShieldCheck className="w-4 h-4" />{" "}{_t("Verifica pubblica")}</a>
        </div>
      </div>
    </div>
  );
}

// -------------- Voucher card in opened capsule --------------
function VoucherCard({ content, capsuleId }) {
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemResult, setRedeemResult] = useState(null);
  const [showRedeemForm, setShowRedeemForm] = useState(false);
  const [iban, setIban] = useState("");
  const token = typeof window !== "undefined" ? localStorage.getItem("tc_access_token") : "";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/vouchers/${content.voucher_id}`);
      setVoucher(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [content.voucher_id]);

  const doRedeem = async () => {
    setRedeeming(true);
    try {
      const { data } = await api.post(`/vouchers/${content.voucher_id}/redeem`, { iban, method: "bank_transfer" });
      setRedeemResult(data);
      toast.success(_t("Voucher riscattato"));
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRedeeming(false); }
  };

  if (loading) return <div className="tc-card p-6"><Loader2 className="w-5 h-5 animate-spin text-[#FF5722]" /></div>;
  if (!voucher) return null;

  const isRedeemed = voucher.state === "RISCATTATO";
  const pdfUrl = `${API}/vouchers/${content.voucher_id}/pdf?auth=${encodeURIComponent(token || "")}`;
  const verifyUrl = `/verifica-voucher/${voucher.public_id}`;

  return (
    <div className="tc-card p-6 md:p-8 mb-4 relative overflow-hidden" data-testid={`voucher-card-${content.voucher_id}`}>
      <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#FF5722]/10 blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF5722] to-[#FF8A50] flex items-center justify-center shadow-lg shadow-[#FF5722]/30">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">{_t("Voucher · {0}", voucher.purpose)}</div>
              <div className="font-display text-3xl text-[#FF5722]">€ {voucher.face_value.toFixed(2)}</div>
            </div>
          </div>
          {isRedeemed ? (
            <span className="pill pill-aperta">{_t("Riscattato")}</span>
          ) : voucher.can_redeem ? (
            <span className="pill pill-disponibile">{_t("Pronto")}</span>
          ) : (
            <span className="pill pill-viaggio">{_t("In attesa")}</span>
          )}
        </div>

        {voucher.is_demo && (
          <div className="mb-4 p-2 rounded-lg bg-red-500/10 border border-red-500/25 text-red-200 text-xs text-center">{_t("⚠ DEMO — Nessun valore monetario reale. Simulazione bancaria.")}</div>
        )}

        {voucher.personal_message && (
          <div className="text-white/80 italic mb-4 pb-4 border-b border-white/10">«{voucher.personal_message}»</div>
        )}

        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm mb-6">
          <div className="text-white/50 text-xs">{_t("Numero")}</div>
          <div className="font-mono-launch text-white">{voucher.public_id}</div>
          <div className="text-white/50 text-xs">{_t("Emesso il")}</div>
          <div className="text-white">{new Date(voucher.issued_at).toLocaleDateString(_locale())}</div>
          <div className="text-white/50 text-xs">{_t("Emittente")}</div>
          <div className="text-white">{voucher.bank_name}</div>
          <div className="text-white/50 text-xs">{_t("Firma")}</div>
          <div className="text-white uppercase">{voucher.signature_status}</div>
        </div>

        <div className="flex flex-wrap gap-3">
          <a href={pdfUrl} target="_blank" rel="noreferrer" className="btn-ghost !text-sm" data-testid={`voucher-download-${voucher.id}`}>
            <Download className="w-4 h-4" />{" "}{_t("Scarica PDF")}</a>
          <a href={verifyUrl} target="_blank" rel="noreferrer" className="btn-ghost !text-sm">
            <ShieldCheck className="w-4 h-4" />{" "}{_t("Verifica pubblica")}</a>
          {voucher.can_redeem && !showRedeemForm && !redeemResult && (
            <button onClick={() => setShowRedeemForm(true)} className="btn-launch !text-sm" data-testid={`voucher-redeem-btn-${voucher.id}`}>
              <Sparkles className="w-4 h-4" />{" "}{_t("Riscatta ora")}</button>
          )}
        </div>

        {showRedeemForm && !redeemResult && (
          <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="text-sm text-white/80 mb-3">{_t("Inserisci un IBAN per ricevere il pagamento (demo).")}</div>
            <input data-testid={`voucher-iban-${voucher.id}`} value={iban} onChange={(e) => setIban(e.target.value)} placeholder={_t("IT60 X054 2811 1010 0000 0123 456")} className="tc-input mb-3" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowRedeemForm(false)} className="btn-ghost !text-sm">{_t("Annulla")}</button>
              <button data-testid={`voucher-confirm-redeem-${voucher.id}`} onClick={doRedeem} disabled={redeeming} className="btn-launch !text-sm">
                {redeeming ? <><Loader2 className="w-4 h-4 animate-spin" />{" "}{_t("Riscatto...")}</> : _t("Conferma riscatto")}
              </button>
            </div>
          </div>
        )}

        {redeemResult && (
          <div className="mt-6 p-5 rounded-xl bg-green-500/10 border border-green-500/30" data-testid={`voucher-redeem-result-${voucher.id}`}>
            <div className="text-green-300 font-medium mb-2">{_t("✓ Riscatto completato")}</div>
            <div className="text-sm text-white/80">
              <div>{_t("Importo: € {0}", redeemResult.amount.toFixed(2))}</div>
              <div className="text-xs text-white/50 mt-1">{_t("Riferimento:")}{" "}<span className="font-mono-launch">{redeemResult.payout_reference}</span></div>
              {redeemResult.warning && <div className="mt-2 text-xs text-red-300">{redeemResult.warning}</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PrivateReplyBlock({ capsuleId }) {
  const [status, setStatus] = useState(null); // {already_sent, created_at}
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/capsules/${capsuleId}/private-reply-status`);
        setStatus(data);
      } catch (e) { /* if 404/403 mittente non attivo o non destinatario, nascondi */ setStatus({ hidden: true }); }
    })();
  }, [capsuleId]);

  if (!status || status.hidden) return null;

  if (status.already_sent) {
    return (
      <div data-testid="private-reply-already-sent" className="tc-card p-4 mb-6 border-emerald-400/20 bg-emerald-500/5">
        <div className="text-emerald-200 text-sm">{_t("✉️ Hai già inviato la tua cartolina di ritorno il {0}.", new Date(status.created_at).toLocaleString(_locale()))}</div>
      </div>
    );
  }

  const send = async () => {
    if (text.trim().length < 1) return;
    setSending(true);
    try {
      await api.post(`/capsules/${capsuleId}/private-reply`, { text: text.trim() });
      setStatus({ already_sent: true, created_at: new Date().toISOString() });
      setOpen(false);
      toast.success(_t("Cartolina inviata al mittente originale."));
    } catch (e) { toast.error(formatApiError(e)); }
    setSending(false);
  };

  return (
    <div data-testid="private-reply-block" className="tc-card p-6 mb-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">{_t("Cartolina di ritorno")}</div>
          <div className="text-white/85 text-sm max-w-2xl">{_t("Puoi inviare")}{" "}<strong>{_t("una sola")}</strong>{" "}{_t("risposta privata al mittente originale di questa capsula, come una cartolina di ritorno. Il testo arriverà solo a lui/lei.")}</div>
        </div>
        {!open && (
          <button data-testid="open-private-reply-btn" onClick={() => setOpen(true)} className="btn-primary !py-2 !px-4">{_t("Scrivi la cartolina")}</button>
        )}
      </div>
      {open && (
        <div className="mt-4 space-y-3">
          <textarea
            data-testid="private-reply-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={_t("Grazie, ti ho sentito… (max 2000 caratteri)")}
            rows={4}
            maxLength={2000}
            className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#FF5722]/60"
          />
          <div className="flex items-center justify-between text-xs text-white/40">
            <span>{text.length} / 2000</span>
            <div className="flex gap-2">
              <button data-testid="private-reply-cancel" className="btn-ghost !py-2 !px-3" onClick={() => setOpen(false)}>{_t("Annulla")}</button>
              <button data-testid="private-reply-send" onClick={send} disabled={sending || text.trim().length < 1} className="btn-primary !py-2 !px-4 disabled:opacity-40">
                {sending ? _t("Invio…") : _t("Invia")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

